'use client';
import { useQuery } from '@tanstack/react-query';
import { cva } from 'class-variance-authority';
import { useLocale } from 'next-intl';
import { ComponentProps, Context, createContext, useContext, useMemo, useState } from 'react';
import type { StorefrontItemBody } from '@/api/menu-list/storefront-menu-snapshot.types';
import { getPriceForMenuListItem, buildItemTags } from '@/entities/cart';
import type { ResolvedStorefrontItem } from '@/entities/product/lib/storefront-menu';
import { QueryApi } from '@/features/query/api';
import { localeToLang } from '@/shared/i18n/localeToLang';
import { Link } from '@/shared/i18n/navigation';
import { applyParentFallbacks } from '@/shared/lib/apply-parent-fallbacks';
import { EventService } from '@/shared/lib/event-service';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Country } from '@/shared/model/Country';
import { Language, LocalizedString } from '@/shared/model/Language';
import { Card } from '@/shared/ui/card';
import { Layout } from '@/shared/ui/layout';
import { LinkBox } from '@/shared/ui/link-box';
import { prefabsStore } from '@/shared/ui/modal/prefabs-store';
import { MenuItemContext } from '@/shared/ui/providers/menu-item-provider';
import { ProductCardDefaultVariant } from './product-card-default-variant';
import { ProductCardHoverableVariant } from './product-card-hoverable-variant';
import { ProductCardTransparentVariant } from './product-card-transparent-variant';
import './product-card.scss';
import { ProductCardAddToCartAction, ProductCardProps } from './types';
import { useProductCardAction } from './use-product-card-action';

type ContextValue = ProductCardProps & {
    selectedVariantId?: string;
    setSelectedVariantId: (id: string) => void;
};

// Fixes multiple context instances issue in
// production build with Turbopack (https://github.com/vercel/next.js/issues/89192)
// TODO: Remove after issue is fixed in Turbopack
const PRODUCT_CARD_CONTEXT_KEY = Symbol.for('ProductCardContext');

const globalRegistry = globalThis as typeof globalThis &
    Record<symbol, Context<ContextValue | Record<string, never>>>;

export const ProductCardContext = (globalRegistry[PRODUCT_CARD_CONTEXT_KEY] ??= createContext<
    ContextValue | Record<string, never>
>({} as ContextValue));

export const productCardVariants = cva('product-card', {
    variants: {
        variant: {
            default: 'product-card--default',
            hoverable: 'product-card--hoverable',
            transparent: 'product-card--transparent',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

const ProductCardVariantComponents = {
    default: ProductCardDefaultVariant,
    hoverable: ProductCardHoverableVariant,
    transparent: ProductCardTransparentVariant,
};

export type ProductCardWrapperProps = ComponentProps<typeof Layout> & {
    productCardOptions?: Pick<
        ProductCardProps,
        | 'variant'
        | 'isNameVisible'
        | 'isImageVisible'
        | 'isImageTagsVisible'
        | 'isDescriptionVisible'
        | 'isVariantsSelectVisible'
        | 'isAddToCartButtonVisible'
        | 'isOriginalPriceVisible'
        | 'addToCartAction'
    > & {
        isTimeAvailabilityVisibleInImage?: boolean;
    };
};

type ItemsIndex = Record<string, ResolvedStorefrontItem>;

const getVariantOptions = (
    parentHash: string,
    menuListId: string | null,
    locale: Language,
    itemsIndex?: ItemsIndex,
    parentBody?: StorefrontItemBody,
) => {
    if (!itemsIndex) return [];

    const parentEntry = itemsIndex[parentHash];
    const variantHashes = parentEntry?.placement.variantHashes ?? [];
    if (!variantHashes.length) return [];

    const variantEntries = variantHashes
        .map((variantHash) => itemsIndex[variantHash])
        .filter((entry): entry is ResolvedStorefrontItem => !!entry);

    const defaultVariant = variantEntries.find((entry) => entry.placement.isDefault === true);
    const defaultLabel = defaultVariant?.body.name ?? parentBody?.name;

    const otherVariants = variantEntries
        .map((entry) => {
            if (entry.placement.isDefault === true) return null;
            const label = entry.body.name;
            if (!label?.[locale]) return null;
            return { id: entry.hash, label };
        })
        .filter((v): v is { id: string; label: LocalizedString } => v !== null);

    if (otherVariants.length === 0) return [];

    return [
        {
            id: parentHash,
            label: defaultLabel,
        },
        ...otherVariants,
    ];
};

export const ProductCardWrapper = ({
    children,
    productCardOptions,
    media,
    ...props
}: ProductCardWrapperProps) => {
    const locale = localeToLang(useLocale());
    const { data, itemsIndex } = useContext(MenuItemContext);
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const [selectedCurrency] = useSelectedCurrency();

    const { item, itemId, menuListId, hash, slug, overrideItem } = data;

    const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(() => {
        if (productCardOptions?.variant) return undefined;
        // The parent item is always the default selection — initial data loads from it.
        return hash;
    });

    const variants = useMemo(
        () => getVariantOptions(hash, menuListId, locale, itemsIndex, item),
        [hash, menuListId, locale, itemsIndex, item],
    );

    if (!hash || !slug?.[locale]) {
        return null;
    }

    const venueCountry = (venueQuery.data?.address?.country as Country) ?? Country.CZ;

    const variantEntry =
        selectedVariantId && selectedVariantId !== hash && itemsIndex
            ? itemsIndex[selectedVariantId]
            : undefined;
    const currentBody: StorefrontItemBody =
        variantEntry && variantEntry.placement.type === 'variant'
            ? applyParentFallbacks(variantEntry.body, item)
            : item;

    // The isDefault variant has no price of its own — fall back to the parent menu list item
    // so getPriceForMenuListItem doesn't return null and hide the card.
    const isDefaultVariantSelected =
        !!variantEntry &&
        variantEntry.placement.type === 'variant' &&
        variantEntry.placement.isDefault === true;
    const priceMenuListItem = isDefaultVariantSelected
        ? { item, overrideItem }
        : variantEntry
          ? {
                item: variantEntry.body,
                overrideItem: variantEntry.placement.overridePrices?.length
                    ? { prices: variantEntry.placement.overridePrices }
                    : undefined,
            }
          : { item, overrideItem };

    const priceValue = getPriceForMenuListItem(priceMenuListItem, venueCountry, selectedCurrency);

    if (!priceValue) {
        return null;
    }

    // Priority: 1) per-instance override from prefab options (supports 'selectVenue' in venue router),
    // 2) global productDetail.openMode mapped to addToCart/viewDetail,
    // 3) fallback 'viewDetail' (matches the editor selector default of 'page').
    const globalOpenMode = prefabsStore.getProductDetail()?.openMode;
    const globalAction: ProductCardAddToCartAction | undefined =
        globalOpenMode === 'page' ? 'viewDetail' : globalOpenMode === 'modal' ? 'addToCart' : undefined;
    const addToCartAction: ProductCardAddToCartAction =
        productCardOptions?.addToCartAction ?? globalAction ?? 'viewDetail';

    const contextValue: ContextValue = {
        id: itemId || hash,
        imageUrl: currentBody.assets?.find(({ isMainAsset }) => isMainAsset)?.fullUrl ?? undefined,
        tags: buildItemTags(item.tags),
        name: item.name,
        description: currentBody.shortDescription,
        variants,
        priceValues: [
            {
                amount: priceValue.amount,
                currency: selectedCurrency,
            },
        ],
        slug: slug[locale],
        hash,
        isImageVisible: productCardOptions?.isImageVisible ?? true,
        isNameVisible: productCardOptions?.isNameVisible ?? true,
        isDescriptionVisible: productCardOptions?.isDescriptionVisible ?? true,
        isVariantsSelectVisible: productCardOptions?.isVariantsSelectVisible ?? true,
        isOriginalPriceVisible: productCardOptions?.isOriginalPriceVisible ?? true,
        isAddToCartButtonVisible: productCardOptions?.isAddToCartButtonVisible ?? true,
        isImageTagsVisible: productCardOptions?.isImageTagsVisible,
        variant: productCardOptions?.variant,
        addToCartAction,
        selectedVariantId,
        setSelectedVariantId,
    };

    return (
        <ProductCardContext.Provider value={contextValue}>
            <ProductCardWrapperShell media={media} {...props}>
                {children}
            </ProductCardWrapperShell>
        </ProductCardContext.Provider>
    );
};

type ProductCardWrapperShellProps = ComponentProps<typeof Layout>;

const ProductCardWrapperShell = ({ children, media, ...props }: ProductCardWrapperShellProps) => {
    const { href, trigger, addToCartAction } = useProductCardAction();

    // Only "viewDetail" renders a real link to the detail page.
    // "addToCart" and "selectVenue" open a modal via onClick — they must not be anchor links.
    if (addToCartAction === 'viewDetail') {
        return (
            <Layout
                element={LinkBox}
                href={href}
                data-testid="product-card"
                media={{
                    ...media,
                    initial: {
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 'var(--radius-card)',
                        overflow: 'hidden',
                        ...media?.initial,
                    },
                }}
                {...props}
            >
                {children}
            </Layout>
        );
    }

    return (
        <Layout
            element="div"
            data-testid="product-card"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                trigger();
            }}
            media={{
                ...media,
                initial: {
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 'var(--radius-card)',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    ...media?.initial,
                },
            }}
            {...props}
        >
            {children}
        </Layout>
    );
};

export const ProductCard = ({
    id,
    variant,
    className,
    isImageVisible = true,
    imageUrl,
    isImageTagsVisible,
    tags,
    name,
    isNameVisible = true,
    availability,
    description,
    isDescriptionVisible = true,
    variants,
    isVariantsSelectVisible = true,
    defaultVariantId,
    priceValues,
    isOriginalPriceVisible = true,
    isAddToCartButtonVisible = true,
    slug,
    hash,
    addToCartAction = 'addToCart',
    ...cardProps
}: ProductCardProps) => {
    const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(
        defaultVariantId || variants?.[0]?.id,
    );
    const VariantComponent = ProductCardVariantComponents[variant ?? 'default'];
    const props = {
        id,
        isImageVisible,
        imageUrl,
        tags,
        name,
        isNameVisible,
        isImageTagsVisible,
        availability,
        description,
        isDescriptionVisible,
        variants,
        isVariantsSelectVisible,
        defaultVariantId,
        priceValues,
        isOriginalPriceVisible,
        isAddToCartButtonVisible,
        slug,
        hash,
        addToCartAction,
    };

    return (
        <ProductCardContext.Provider value={{ ...props, selectedVariantId, setSelectedVariantId }}>
            <ProductCardStandaloneShell
                className={productCardVariants({ variant, className })}
                VariantComponent={VariantComponent}
                variantProps={props}
                cardProps={cardProps}
            />
        </ProductCardContext.Provider>
    );
};

type ProductCardStandaloneShellProps = {
    className: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    VariantComponent: any;
    variantProps: Record<string, unknown>;
    cardProps: Record<string, unknown>;
};

const ProductCardStandaloneShell = ({
    className,
    VariantComponent,
    variantProps,
    cardProps,
}: ProductCardStandaloneShellProps) => {
    const { trigger, href, addToCartAction } = useProductCardAction();
    const { id, slug, hash, selectedVariantId } = useContext(ProductCardContext);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const CardComponent = Card as any;

    if (addToCartAction === 'selectVenue') {
        return (
            <CardComponent
                as="div"
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    trigger();
                }}
                className={className}
                data-testid="product-card"
                data-id={id}
                shadow
                style={{ cursor: 'pointer' }}
                {...cardProps}
            >
                <VariantComponent {...variantProps} />
            </CardComponent>
        );
    }

    return (
        <CardComponent
            as={Link}
            href={href}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                EventService.trigger('product:view-detail', {
                    slug: `${slug}-${hash}`,
                    variantHash: selectedVariantId,
                });
            }}
            className={className}
            data-testid="product-card"
            data-id={id}
            shadow
            {...cardProps}
        >
            <VariantComponent {...variantProps} />
        </CardComponent>
    );
};

export const Wrapper = ProductCardWrapper;
export const DefaultVariant = ProductCardDefaultVariant;
export const HoverableVariant = ProductCardHoverableVariant;
export const TransparentVariant = ProductCardTransparentVariant;
export { ProductCardAvailability } from './product-card-availability';
export { ProductCardBottomOverlayWrapper } from './product-card-bottom-overlay-wrapper';
export { ProductCardContentWrapper } from './product-card-content-wrapper';
export { ProductCardDescription } from './product-card-description';
export { ProductCardImage } from './product-card-image';
export { ProductCardName } from './product-card-name';
export { ProductCardPrice } from './product-card-price';
export { ProductCardOriginalPrice } from './product-card-original-price';
export {
    ProductCardAddToCartButton,
    Icon as ProductCardAddToCartButtonIcon,
    Text as ProductCardAddToCartButtonText,
} from './product-card-add-to-cart-button';
export { ProductCardSelect } from './product-card-select';
export { ProductCardTags } from './product-card-tags';
